/**
 * Review Admin API — thin wrappers around reviews_admin backend endpoints.
 *
 * Uses getERPClient().call() for mode-aware API calls (JWT or session).
 * Tenant is read from auth store at call time (late-binding).
 */

import { getERPClient } from "@/lib/erp/client"
import { useAuthStore } from "@/lib/store/authStore"
import type {
  ReviewListResponse,
  ReviewModerateResponse,
  ReviewReplyResponse,
  ReviewDeleteReplyResponse,
  ReviewDeleteResponse,
  ReviewBulkModerateResponse,
  ReviewStatus,
} from "./reviewTypes"

const API_BASE = "zaviago_backend.storefront.reviews_admin"

function getTenant(): string {
  const tenant = useAuthStore.getState().currentTenant
  if (!tenant) throw new Error("No tenant selected")
  return tenant
}

export const reviewApi = {
  list(
    status: ReviewStatus = "Pending",
    page = 1,
    limit = 20,
    search?: string,
  ) {
    const client = getERPClient()
    return client.call<ReviewListResponse>(`${API_BASE}.admin_list_reviews`, {
      tenant: getTenant(),
      status: status === "All" ? undefined : status,
      page,
      limit,
      search: search || undefined,
    })
  },

  moderate(reviewName: string, action: "approve" | "reject") {
    const client = getERPClient()
    return client.call<ReviewModerateResponse>(
      `${API_BASE}.admin_moderate`,
      {
        tenant: getTenant(),
        review_name: reviewName,
        action,
      },
    )
  },

  reply(reviewName: string, replyText: string) {
    const client = getERPClient()
    return client.call<ReviewReplyResponse>(`${API_BASE}.admin_reply`, {
      tenant: getTenant(),
      review_name: reviewName,
      reply_text: replyText,
    })
  },

  // Clears the seller reply (backend admin_delete_reply returns { name, success }).
  deleteReply(reviewName: string) {
    const client = getERPClient()
    return client.call<ReviewDeleteReplyResponse>(
      `${API_BASE}.admin_delete_reply`,
      {
        tenant: getTenant(),
        review_name: reviewName,
      },
    )
  },

  delete(reviewName: string) {
    const client = getERPClient()
    return client.call<ReviewDeleteResponse>(`${API_BASE}.admin_delete`, {
      tenant: getTenant(),
      review_name: reviewName,
    })
  },

  bulkModerate(reviewNames: string[], action: "approve" | "reject") {
    const client = getERPClient()
    return client.call<ReviewBulkModerateResponse>(
      `${API_BASE}.admin_bulk_moderate`,
      {
        tenant: getTenant(),
        review_names: JSON.stringify(reviewNames),
        action,
      },
    )
  },
}
